import { useMemo, useState } from 'react'
import EditDetailsModal from './EditDetailsModal'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'

export default function AccountSettings() {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const { user } = useAuth()

  const userData = useMemo(() => {
    const fullName = getUserDisplayName(user)
    const email = user?.email || '—'
    const phone = user?.phoneNumber || '—'
    const gender = user?.gender || ''
    const state = user?.state || ''
    const country = user?.country || ''
    const location = (user as { location?: string })?.location || ''
    const tribe = (user as { tribe?: string })?.tribe || ''
    const genotype = (user as { genotype?: string })?.genotype || ''
    const dateOfBirth = (user as { dateOfBirth?: string })?.dateOfBirth || ''
    const status = user ? 'Active' : 'Inactive'
    const firstName = user?.firstName || fullName.split(' ')[0] || ''
    const lastName = user?.lastName || fullName.split(' ').slice(1).join(' ')
    return {
      fullName,
      firstName,
      lastName,
      gender,
      email,
      phone,
      state,
      country,
      location,
      tribe,
      genotype,
      dateOfBirth,
      status,
    }
  }, [user])

  const initials = getUserInitials(user)

  return (
    <>
      <div className='w-full'>
        <h3 className='text-[18px] font-bold text-[#151515] mb-6'>Personal Details</h3>

        <div className='flex flex-col md:flex-row gap-8'>
          {/* Left: Avatar and Edit Button */}
          <div className='flex flex-col items-start gap-4'>
            <div className='h-[80px] w-[80px] rounded-full bg-[#FA6017] flex items-center justify-center'>
              <span className='text-[28px] font-bold text-white'>{initials}</span>
            </div>
            <button
              onClick={() => setIsEditModalOpen(true)}
              className='px-4 py-2 border border-[#151515] text-[#151515] rounded-[8px] font-medium text-[14px] hover:bg-gray-50 transition-colors'
            >
              Edit Details
            </button>
          </div>

          {/* Right: User Details */}
          <div className='flex flex-col gap-3'>
            <div className='flex items-center gap-1'>
              <span className='text-[14px] font-normal text-[#4e4d4d]'>Fullname:</span>
              <span className='text-[14px] font-semibold text-[#151515]'>{userData.fullName}</span>
            </div>
            <div className='flex items-center gap-1'>
              <span className='text-[14px] font-normal text-[#4e4d4d]'>Gender:</span>
              <span className='text-[14px] font-semibold text-[#151515]'>{userData.gender}</span>
            </div>
            <div className='flex items-center gap-1'>
              <span className='text-[14px] font-normal text-[#4e4d4d]'>Email:</span>
              <span className='text-[14px] font-semibold text-[#151515]'>{userData.email}</span>
            </div>
            <div className='flex items-center gap-1'>
              <span className='text-[14px] font-normal text-[#4e4d4d]'>Phone number:</span>
              <span className='text-[14px] font-semibold text-[#151515]'>{userData.phone}</span>
            </div>
            <div className='flex items-center gap-1'>
              <span className='text-[14px] font-normal text-[#4e4d4d]'>State:</span>
              <span className='text-[14px] font-semibold text-[#151515]'>{userData.state}</span>
            </div>
            <div className='flex items-center gap-1'>
              <span className='text-[14px] font-normal text-[#4e4d4d]'>Country:</span>
              <span className='text-[14px] font-semibold text-[#151515]'>{userData.country}</span>
            </div>
            <div className='flex items-center gap-1'>
              <span className='text-[14px] font-normal text-[#4e4d4d]'>Status:</span>
              <span className='inline-flex items-center px-2 py-0.5 rounded-[4px] bg-[#ECFDF3] text-[#027A48] text-[12px] font-medium'>
                {userData.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Details Modal */}
      <EditDetailsModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        userData={userData}
        userId={user?.id ?? ''}
      />
    </>
  )
}
